import { UnfoldMore } from '@mui/icons-material'
import { Box, Menu, MenuItem, Typography } from '@mui/material'
import { FC, useState } from 'react'
import {
    type CandidateSortField,
    type SortOrder,
    type SortState,
} from 'shared/hooks/candidateSearchUrlState'

interface NameColumnHeaderProps {
    sort: SortState
    onSortChange: (sort: SortState) => void
}

export const NameColumnHeader: FC<NameColumnHeaderProps> = ({
    sort,
    onSortChange,
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleSort = (
        field: CandidateSortField | null,
        order: SortOrder | null
    ) => {
        onSortChange(field && order ? [{ field, order }] : [])
        handleClose()
    }

    const firstSort = sort[0]
    const currentLabel = (() => {
        if (!firstSort) return 'Candidate'
        if (firstSort.field === 'firstName') {
            return firstSort.order === 'asc' ? 'First A-Z' : 'First Z-A'
        }
        if (firstSort.field === 'lastName') {
            return firstSort.order === 'asc' ? 'Last A-Z' : 'Last Z-A'
        }
        return 'Candidate'
    })()

    const isNameSort =
        firstSort?.field === 'firstName' || firstSort?.field === 'lastName'

    return (
        <>
            <Box
                data-testid="candidate-name-sort"
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                        '& .sort-icon': {
                            opacity: 1,
                        },
                    },
                }}
            >
                <Typography
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'text.primary',
                    }}
                >
                    {currentLabel}
                </Typography>
                <UnfoldMore
                    className="sort-icon"
                    sx={{
                        fontSize: '1rem',
                        ml: 0.25,
                        color: isNameSort ? 'primary.main' : 'text.secondary',
                        opacity: isNameSort ? 1 : 0,
                        transition: 'opacity 0.15s',
                    }}
                />
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 140,
                            '& .MuiMenuItem-root': {
                                fontSize: '0.75rem',
                                py: 0.75,
                            },
                        },
                    },
                }}
            >
                <MenuItem onClick={() => handleSort(null, null)}>
                    Default
                </MenuItem>
                <MenuItem onClick={() => handleSort('firstName', 'asc')}>
                    First name A-Z
                </MenuItem>
                <MenuItem onClick={() => handleSort('firstName', 'desc')}>
                    First name Z-A
                </MenuItem>
                <MenuItem onClick={() => handleSort('lastName', 'asc')}>
                    Last name A-Z
                </MenuItem>
                <MenuItem onClick={() => handleSort('lastName', 'desc')}>
                    Last name Z-A
                </MenuItem>
            </Menu>
        </>
    )
}
